import { useEffect, useRef, type RefObject } from 'react';

interface UsePopoverDismissOptions {
    open: boolean;
    onClose: () => void;
    // Clicks inside these (the popover and its toggle button) do not dismiss.
    insideRefs: ReadonlyArray<RefObject<HTMLElement>>;
}

// Outside mousedown or Escape closes an open popover.
export function usePopoverDismiss({ open, onClose, insideRefs }: UsePopoverDismissOptions): void {
    const onCloseRef = useRef(onClose);
    const insideRefsRef = useRef(insideRefs);

    useEffect(() => {
        onCloseRef.current = onClose;
        insideRefsRef.current = insideRefs;
    }, [onClose, insideRefs]);

    useEffect(() => {
        if (!open) return;
        const onDown = (e: MouseEvent) => {
            const target = e.target as Node;
            if (insideRefsRef.current.some((ref) => ref.current?.contains(target))) return;
            onCloseRef.current();
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onCloseRef.current();
        };
        document.addEventListener('mousedown', onDown);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDown);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);
}
